import { Chart, registerables } from "chart.js";
import { COLORS } from "./pdfStyles";

// Register all Chart.js components
Chart.register(...registerables);

/**
 * Renders a chart to canvas and returns a base64 image
 * @param {Object} config - Chart.js configuration
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @returns {Promise<string>} Base64 image data URL
 */
export const renderChartToImage = async (config, width = 500, height = 250) => {
  return new Promise((resolve, reject) => {
    try {
      // Create an off-screen canvas
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      // Set white background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);

      // Create the chart
      const chart = new Chart(ctx, {
        ...config,
        options: {
          ...config.options,
          responsive: false,
          maintainAspectRatio: false,
          animation: false, // Disable animation for immediate rendering
        },
      });

      // Small delay to ensure chart is fully rendered
      setTimeout(() => {
        const imageData = canvas.toDataURL("image/png", 1.0);
        chart.destroy(); // Clean up
        resolve(imageData);
      }, 100);
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Aggregates sleep data by week for trend analysis
 * @param {Array} sleepData - Raw sleep data
 * @returns {Object} Aggregated data with labels and values
 */
export const aggregateSleepByWeek = (sleepData) => {
  if (!sleepData || sleepData.length === 0) {
    return { labels: [], durations: [], avgByWeek: [] };
  }

  // Sort by date
  const sorted = [...sleepData].sort(
    (a, b) => new Date(a.date || a.start_time) - new Date(b.date || b.start_time)
  );

  // Group by week
  const weeklyData = {};
  sorted.forEach((item) => {
    const date = new Date(item.date || item.start_time);
    const weekStart = getWeekStart(date);
    const weekKey = weekStart.toISOString().split("T")[0];

    if (!weeklyData[weekKey]) {
      weeklyData[weekKey] = {
        durations: [],
        light: [],
        deep: [],
        rem: [],
      };
    }

    if (item.duration) weeklyData[weekKey].durations.push(item.duration);
    if (item.light) weeklyData[weekKey].light.push(item.light);
    if (item.deep) weeklyData[weekKey].deep.push(item.deep);
    if (item.rem) weeklyData[weekKey].rem.push(item.rem);
  });

  // Calculate averages per week
  const labels = [];
  const avgDurations = [];
  const avgLight = [];
  const avgDeep = [];
  const avgRem = [];

  Object.keys(weeklyData)
    .sort()
    .forEach((weekKey) => {
      const week = weeklyData[weekKey];
      const date = new Date(weekKey);
      labels.push(formatWeekLabel(date));

      avgDurations.push(
        week.durations.length > 0
          ? Math.round(average(week.durations) / 60) // Convert to hours
          : 0
      );
      avgLight.push(
        week.light.length > 0 ? Math.round(average(week.light) / 60) : 0
      );
      avgDeep.push(
        week.deep.length > 0 ? Math.round(average(week.deep) / 60) : 0
      );
      avgRem.push(
        week.rem.length > 0 ? Math.round(average(week.rem) / 60) : 0
      );
    });

  return { labels, avgDurations, avgLight, avgDeep, avgRem };
};

/**
 * Creates a sleep trend chart configuration
 * @param {Array} sleepData - Raw sleep data
 * @returns {Object} Chart.js configuration
 */
export const createSleepTrendChartConfig = (sleepData) => {
  const { labels, avgDurations, avgLight, avgDeep, avgRem } =
    aggregateSleepByWeek(sleepData);

  return {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          label: "Deep Sleep (hrs)",
          data: avgDeep,
          backgroundColor: COLORS.primary.main,
          borderColor: COLORS.primary.dark,
          borderWidth: 1,
          stack: "sleep",
        },
        {
          label: "REM Sleep (hrs)",
          data: avgRem,
          backgroundColor: COLORS.secondary.main,
          borderColor: COLORS.secondary.dark,
          borderWidth: 1,
          stack: "sleep",
        },
        {
          label: "Light Sleep (hrs)",
          data: avgLight,
          backgroundColor: COLORS.primary.light,
          borderColor: COLORS.primary.main,
          borderWidth: 1,
          stack: "sleep",
        },
      ],
    },
    options: {
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: "Weekly Sleep Trends",
          font: { size: 14, weight: "bold" },
          color: COLORS.primary.main,
        },
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 12,
            padding: 10,
            font: { size: 9 },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          ticks: {
            font: { size: 8 },
            maxRotation: 45,
            minRotation: 45,
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          title: {
            display: true,
            text: "Hours",
            font: { size: 10 },
          },
          ticks: {
            font: { size: 9 },
          },
        },
      },
    },
  };
};

/**
 * Creates a sleep duration line chart configuration
 * @param {Array} sleepData - Raw sleep data
 * @returns {Object} Chart.js configuration
 */
export const createSleepDurationChartConfig = (sleepData) => {
  const { labels, avgDurations } = aggregateSleepByWeek(sleepData);

  return {
    type: "line",
    data: {
      labels,
      datasets: [
        {
          label: "Avg Sleep Duration (hrs)",
          data: avgDurations,
          borderColor: COLORS.primary.main,
          backgroundColor: COLORS.primary.light + "80",
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          pointBackgroundColor: COLORS.primary.main,
        },
      ],
    },
    options: {
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: "Average Weekly Sleep Duration",
          font: { size: 14, weight: "bold" },
          color: COLORS.primary.main,
        },
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            font: { size: 8 },
            maxRotation: 45,
            minRotation: 45,
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: "Hours",
            font: { size: 10 },
          },
          ticks: {
            font: { size: 9 },
          },
        },
      },
    },
  };
};

/**
 * Aggregates exercise data by week for trend analysis
 * @param {Array} exerciseData - Raw exercise data
 * @returns {Object} Aggregated data with labels and values
 */
export const aggregateExerciseByWeek = (exerciseData) => {
  if (!exerciseData || exerciseData.length === 0) {
    return { labels: [], light: [], moderate: [], vigorous: [], sedentary: [] };
  }

  // Sort by date
  const sorted = [...exerciseData].sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );

  // Group by week
  const weeklyData = {};
  sorted.forEach((item) => {
    const date = new Date(item.date);
    const weekStart = getWeekStart(date);
    const weekKey = weekStart.toISOString().split("T")[0];

    if (!weeklyData[weekKey]) {
      weeklyData[weekKey] = {
        light: [],
        moderate: [],
        vigorous: [],
        sedentary: [],
      };
    }

    if (item.light) weeklyData[weekKey].light.push(item.light);
    if (item.moderate) weeklyData[weekKey].moderate.push(item.moderate);
    if (item.vigorous) weeklyData[weekKey].vigorous.push(item.vigorous);
    if (item.sedentary) weeklyData[weekKey].sedentary.push(item.sedentary);
  });

  // Calculate averages per week
  const labels = [];
  const avgLight = [];
  const avgModerate = [];
  const avgVigorous = [];

  Object.keys(weeklyData)
    .sort()
    .forEach((weekKey) => {
      const week = weeklyData[weekKey];
      const date = new Date(weekKey);
      labels.push(formatWeekLabel(date));

      avgLight.push(
        week.light.length > 0 ? Math.round(average(week.light)) : 0
      );
      avgModerate.push(
        week.moderate.length > 0 ? Math.round(average(week.moderate)) : 0
      );
      avgVigorous.push(
        week.vigorous.length > 0 ? Math.round(average(week.vigorous)) : 0
      );
    });

  return { labels, avgLight, avgModerate, avgVigorous };
};

/**
 * Creates an exercise trend chart configuration
 * @param {Array} exerciseData - Raw exercise data
 * @returns {Object} Chart.js configuration
 */
export const createExerciseTrendChartConfig = (exerciseData) => {
  const { labels, avgLight, avgModerate, avgVigorous } =
    aggregateExerciseByWeek(exerciseData);

  return {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          label: "Vigorous (min)",
          data: avgVigorous,
          backgroundColor: "#d32f2f",
          borderColor: "#b71c1c",
          borderWidth: 1,
          stack: "activity",
        },
        {
          label: "Moderate (min)",
          data: avgModerate,
          backgroundColor: COLORS.secondary.main,
          borderColor: COLORS.secondary.dark,
          borderWidth: 1,
          stack: "activity",
        },
        {
          label: "Light (min)",
          data: avgLight,
          backgroundColor: COLORS.primary.light,
          borderColor: COLORS.primary.main,
          borderWidth: 1,
          stack: "activity",
        },
      ],
    },
    options: {
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: "Weekly Exercise Activity",
          font: { size: 14, weight: "bold" },
          color: COLORS.primary.main,
        },
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 12,
            padding: 10,
            font: { size: 9 },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          ticks: {
            font: { size: 8 },
            maxRotation: 45,
            minRotation: 45,
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          title: {
            display: true,
            text: "Minutes",
            font: { size: 10 },
          },
          ticks: {
            font: { size: 9 },
          },
        },
      },
    },
  };
};

/**
 * Creates a total activity line chart configuration
 * @param {Array} exerciseData - Raw exercise data
 * @returns {Object} Chart.js configuration
 */
export const createActivityTrendChartConfig = (exerciseData) => {
  const { labels, avgLight, avgModerate, avgVigorous } =
    aggregateExerciseByWeek(exerciseData);

  // Calculate total active minutes (light + moderate + vigorous)
  const totalActive = labels.map((_, i) => avgLight[i] + avgModerate[i] + avgVigorous[i]);

  return {
    type: "line",
    data: {
      labels,
      datasets: [
        {
          label: "Total Active Minutes",
          data: totalActive,
          borderColor: COLORS.secondary.main,
          backgroundColor: COLORS.secondary.light + "80",
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          pointBackgroundColor: COLORS.secondary.main,
        },
      ],
    },
    options: {
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: "Average Weekly Active Minutes",
          font: { size: 14, weight: "bold" },
          color: COLORS.primary.main,
        },
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            font: { size: 8 },
            maxRotation: 45,
            minRotation: 45,
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: "Minutes",
            font: { size: 10 },
          },
          ticks: {
            font: { size: 9 },
          },
        },
      },
    },
  };
};

/**
 * Creates a risk factors chart configuration
 * @param {Array} riskRules - Risk rules data with factors
 * @returns {Object} Chart.js configuration
 */
export const createRiskFactorsChartConfig = (riskRules) => {
  if (!riskRules || riskRules.length === 0) {
    return null;
  }

  // Extract factor names and activation values
  const factors = [];
  const values = [];
  const colors = [];

  riskRules.forEach((rule) => {
    if (rule.rule_factor) {
      let factorObj = rule.rule_factor;
      // Parse if it's a string
      if (typeof factorObj === "string") {
        try {
          factorObj = JSON.parse(factorObj);
        } catch (e) {
          return;
        }
      }

      // Get the factor name (first non-null key)
      Object.keys(factorObj).forEach((key) => {
        if (factorObj[key]) {
          factors.push(formatFactorName(key));
          values.push(rule.activation_value || 0);
          // Color based on risk level
          const riskColor = getRiskChartColor(rule.rule_risk);
          colors.push(riskColor);
        }
      });
    }
  });

  if (factors.length === 0) {
    return null;
  }

  return {
    type: "bar",
    data: {
      labels: factors,
      datasets: [
        {
          label: "Risk Contribution",
          data: values,
          backgroundColor: colors,
          borderColor: colors.map((c) => c.replace("0.7", "1")),
          borderWidth: 1,
        },
      ],
    },
    options: {
      indexAxis: "y",
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: "Risk Contributing Factors",
          font: { size: 14, weight: "bold" },
          color: COLORS.primary.main,
        },
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          max: 1,
          title: {
            display: true,
            text: "Activation Value",
            font: { size: 10 },
          },
          ticks: {
            font: { size: 9 },
          },
        },
        y: {
          ticks: {
            font: { size: 8 },
          },
        },
      },
    },
  };
};

// Helper function to format factor names
const formatFactorName = (key) => {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

// Helper function to get color based on risk level
const getRiskChartColor = (riskLevel) => {
  const riskColors = {
    "Very Low": "rgba(76, 175, 80, 0.7)",
    "Low": "rgba(139, 195, 74, 0.7)",
    "Medium": "rgba(255, 235, 59, 0.7)",
    "High": "rgba(255, 152, 0, 0.7)",
    "Very High": "rgba(244, 67, 54, 0.7)",
  };
  return riskColors[riskLevel] || "rgba(158, 158, 158, 0.7)";
};

// Helper functions
const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
  return new Date(d.setDate(diff));
};

const formatWeekLabel = (date) => {
  const month = date.toLocaleString("en-US", { month: "short" });
  const day = date.getDate();
  return `${month} ${day}`;
};

const average = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;

export default renderChartToImage;
